import { Document, Page, pdf } from "@react-pdf/renderer";
import { useEffect, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import { useNavigate, useParams } from "react-router";
import { Link } from "react-router-dom";
import { LoadingComponent } from "../components/LoadingComponent";
import { useProfile } from "../components/ProfileContext";
import { Inspection } from "../models/Inspection";
import { User } from "../models/User";
import { getDB } from "../util/db";
import { afterRenderDelay, arrayToCSV, downloadBlobToNewWindow, downloadPdfDocumentToNewWindow, formatFilename, navigateToInspectionId, pageReadyCondition, setNavAdvisoryLockout, stringToDate, useIsVault } from "../util/utils";
import { Api } from "../util/Api";
import { SpinnerButton } from "../components/SpinnerButton";
import { InspectionPDFRenderer } from "../pages/InspectionPDFRenderer";
import { GroupingDisplay } from "../components/GroupingDisplay";
import { FiletypeCsv, FiletypePdf, Search, Share } from "react-bootstrap-icons";
import { RentalStatusDisplay } from "../components/RentalStatusDisplay";
import { SharingStatus } from "../components/SharingStatus";
import { Timestamp } from "../components/Timestamp";
import { inspectionInterval } from "../dependencies";
import { SharingFormModal } from "../forms/SharingFormModal";
import { Job } from "../models/Job";
import { ComponentType } from "../models/ComponentType";

export function JobPage(props) {
	setNavAdvisoryLockout(false);

	let params = useParams();
	let [job, setJob] = useState(null);
	let [profile, setProfile] = useProfile();
	let isVault = useIsVault();
	let [showSearchBar, setShowSearchBar] = useState(isVault);
	let [showSharingDialog, setShowSharingDialog] = useState(false);
	let [searchResults, setSearchResults] = useState(null);
	let [isPdfGenerating, setIsPdfGenerating] = useState(false);
	let [isCsvGenerating, setIsCsvGenerating] = useState(false);
	let dataGroupingsRef = useRef();
	let navigate = useNavigate();

	useEffect(async () => {
		if(job != null) {
			return;
		}
		let myJob = await Job.find(params.jobId);
		setJob(myJob);
		let inspectionHeaders = await Api.forProfile(profile).inspectionCall(`/v2/inspections?${new URLSearchParams({jobId: myJob.id})}`);
		console.log(myJob.id);
		console.log("INSPECTION LIST");
		console.log(inspectionHeaders);
		let ctypeLookup = await ComponentType.getComponentTypeLookup();
		for(const inspHeader of inspectionHeaders) {
			Inspection.flattenComponentsFromHeader(inspHeader, ctypeLookup); // Make model/serial/ctype searchable
		}
		setSearchResults(inspectionHeaders);
	});

	let expirationCutoffMillis = Date.now() - inspectionInterval;
	let dFormatter = profile.dateFormatter;

	let groupingOptions = [
		{
			name: "model",
			keyPath: ["componentModelNumber"],
			label: "Model #"
		},
		{
			name: "component",
			keyPath: ["componentTypeNames"],
			label: "Component"
		},
		{
			name: "result",
			keyPath: ["result"],
			label: "Result"
		},
		{
			name: "disposition",
			keyPath: ["disposition"],
			label: "Disposition"
		},
		{
			name: "location",
			keyPath: ["location"],
			label: "Location"
		},
		{
			name: "company",
			keyPath: ["companyName"],
			label: "Company"
		}
	];
	if(profile.rentalMode) {
		groupingOptions.push({
			name: "rental",
			keyPath: ["rentalDisposition"],
			label: "Check In/Out"
		});
	}

	let filterKeyPaths = [
		["componentAssetNumber"],
		["companyName"],
		["location"],
		["modelNumbers"],
		["serialNumbers"],
		["componentTypeNames"]
	];

	let sortingOptions = [
		{
			name: "date",
			keyPath: ["completedAt"],
			label: "Inspection Date"
		},
		{
			name: "asset",
			keyPath: ["componentAssetNumber"],
			label: "Asset #"
		}
	];

	let getInspectionTableHeader = (grp) => {
		return <tr>			
			<th></th>
			<th>Asset</th>
			<th>Serial</th>
			<th>Model</th>
			<th>Component</th>
			<th>Last&nbsp;Insp</th>
			<th>Result</th>
			<th>Disposition</th>
		</tr>;
	};

	function JobInspectionTableRow(props) {
		let {
			group: grp,
			inspection: insp
		} = props;
		(grp, insp)
		let [isInspectionLoading, setIsInspectionLoading] = useState(false);
		let viewInspectionAction = () => {
			setIsInspectionLoading(true);
	
			afterRenderDelay(async () => {
				try {
					await navigateToInspectionId(profile, navigate, insp.id)
				} catch(err) {			
					alert(`Unable to load inspection: ${err}`);
					setIsInspectionLoading(false);
				}	
			});
		};

		return <tr key={insp.id}>
			<td><SpinnerButton spinning={isInspectionLoading} onClick={viewInspectionAction}>Show</SpinnerButton></td>
			<td>{insp.componentAssetNumber} <RentalStatusDisplay inspection={insp} /> <SharingStatus ownerUid={insp.userIdentifier} profile={profile} childAutoAccess={false} /></td>
			<td>{insp.serialNumbers}</td>
			<td>{insp.modelNumbers}</td>
			<td>{insp.componentTypeNames}</td>
			<td><Timestamp dateString={insp.completedAt} expirationCutoffMillis={expirationCutoffMillis} formatter={dFormatter} /></td>
			<td>{insp.result}</td>
			<td>{insp.disposition}</td>
		</tr>;
	}

	let getInspectionTableRow = (grp, insp) => {
		return <JobInspectionTableRow group={grp} inspection={insp} />;
	};

	let downloadCsvAction = () => {
		setIsCsvGenerating(true);

		afterRenderDelay(async () => {
			let data = [];
			dataGroupingsRef.current.forEach(grouping => {
				if(grouping != null) {
					data.push([grouping.key])
					data.push(["Asset #", "Model #", "Serial #", "Result", "Disposition", "Timestamp", "Company", "Location", "Rental Disposition"]);
					grouping.data.forEach(insp => {
						data.push([insp.componentAssetNumber, insp.componentModelNumber, insp.componentSerialNumber, insp.result, insp.disposition, profile.dateFormatter(stringToDate(insp.completedAt)), insp.companyName, insp.location, insp.rentalDisposition]);
					})
				}
			});
			
			let csvData = arrayToCSV(data);
			let csvBlob = new Blob([csvData], {type:"text/csv"});
			downloadBlobToNewWindow(csvBlob, formatFilename("csv", profile.dateFormatter(new Date()), profile.job?.name, "assets"));
			setIsCsvGenerating(false);	
		});
	};

	let downloadPdfAction = () => {
		setIsPdfGenerating(true);
		afterRenderDelay(async () => {
			try {
				let inspectionHeaders = [];
				dataGroupingsRef.current.forEach(grouping => {
					if(grouping != null) {
						grouping.data.forEach(insp => {
							inspectionHeaders.push(insp);
						})
					}
				});
	
				let pdf = await Inspection.generatePdfDocumentFromInspectionHeaders(profile, inspectionHeaders);
				await downloadPdfDocumentToNewWindow(pdf, formatFilename("pdf", profile.dateFormatter(new Date()), profile.job?.name, "assets"));
			} catch(err) {
				alert(`Error generating PDF: ${err}`);
			}
			setIsPdfGenerating(false);	
		});
	};

	let shareAction = () => {
		setShowSharingDialog(!showSharingDialog);
	};

	let pageCondition = pageReadyCondition(job != null && searchResults != null);

	if(pageCondition != "ready") {
		return <LoadingComponent />
	}
	
	return <div className="jobPageContainer">
		<h1>
			Job Information
			{job.userIdentifier == profile.id && <Link to={`/jobs/${encodeURIComponent(job.id)}/edit`} className="float-end"><Button variant="success" className="mx-4">Edit</Button></Link>}
			<Button className="mt-3 ms-1 float-end" variant="info" onClick={() => {setShowSearchBar(!showSearchBar)}}><Search /> Tools</Button>
			<SpinnerButton spinning={isPdfGenerating} className="mt-2 float-end filetypeButton" onClick={downloadPdfAction} spinnerStyle={{color: "black"}}><FiletypePdf className="filetypeIcon" /></SpinnerButton>
			<SpinnerButton spinning={isCsvGenerating} className="mt-2 float-end filetypeButton" onClick={downloadCsvAction} spinnerStyle={{color: "black"}}><FiletypeCsv className="filetypeIcon" /></SpinnerButton>
			{job.userIdentifier == profile.id.toString() && <Button className="mt-3 mx-2 float-end" onClick={shareAction}><Share /></Button>}
		</h1>
		<SharingFormModal show={showSharingDialog} setShow={setShowSharingDialog} sharingObject={job} profile={profile} />

		<h2>{job.name}</h2>
		
		<GroupingDisplay data={searchResults} dataGroupingsRef={dataGroupingsRef} sortingOptions={sortingOptions} groupingOptions={groupingOptions} showSearchTools={showSearchBar} headerBuilder={getInspectionTableHeader} rowBuilder={getInspectionTableRow} filterKeyPaths={filterKeyPaths} />

	</div>;
}